import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeader from "../../../components/common/PageHeader";
import ShadowContainer from "../../../components/common/ShadowContainer";
import api from "../../../utils/api";
import "../../../components/Styles/Pages.css";
import { downloadProductTemplate } from "../BulkUpload/utils/downloadProductTemplate";
import { io } from "socket.io-client";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";

const socket = io(process.env.REACT_APP_API_BASE);

function ProductBulkUpload() {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [duplicates, setDuplicates] = useState([]);
  const [errors, setErrors] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const { restaurantId } = useRestaurantConfig();

  const allowedTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv",
  ];

  const navigate = useNavigate();


  useEffect(() => {

    if (!restaurantId) return;

    socket.emit("joinRestaurant", { restaurantId });

    const handleProgress = (data) => {
      const { processed, total, percent } = data;

      setProgress(percent);

      if (processed >= total) {
        setStatus("Finalizing upload...");
      } else {
        setStatus(`Uploading ${processed}/${total}`);
      }
    };

    const handleComplete = () => {
      setProgress(100);
      setStatus("Upload complete");
    };

    socket.on("bulkUploadProgress", handleProgress);
    socket.on("bulkUploadComplete", handleComplete);

    return () => {
      socket.off("bulkUploadProgress", handleProgress);
      socket.off("bulkUploadComplete", handleComplete);
    };

  }, [restaurantId]);

  const templateOptions = [
    {
      label: "Download CSV",
      icon: "pi pi-file",
      command: () => downloadProductTemplate("csv"),
    },
    {
      label: "Download XLS",
      icon: "pi pi-file-excel",
      command: () => downloadProductTemplate("xls"),
    },
    {
      label: "Download XLSX",
      icon: "pi pi-file-excel",
      command: () => downloadProductTemplate("xlsx"),
    },
  ];

  const validateFile = (selectedFile) => {
    if (!selectedFile) return false;

    const isValidType =
      allowedTypes.includes(selectedFile.type) ||
      selectedFile.name.endsWith(".csv") ||
      selectedFile.name.endsWith(".xlsx") ||
      selectedFile.name.endsWith(".xls");

    if (!isValidType) {
      toast.error("Only .xlsx, .xls, and .csv files are allowed");
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0] || null;
    if (validateFile(selectedFile)) {
      setFile(selectedFile);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (validateFile(droppedFile)) {
      setFile(droppedFile);
    }
  }, []);

  const simulateProgress = (start, end, duration = 600) => {
    const stepTime = 20;
    const steps = duration / stepTime;
    let current = start;
    const increment = (end - start) / steps;

    const interval = setInterval(() => {
      current += increment;
      if (current >= end) {
        clearInterval(interval);
        setProgress(end);
      } else {
        setProgress(Math.round(current));
      }
    }, stepTime);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      toast.error("Please select or drop a file to upload");
      return;
    }

    try {
      setStatus("uploading");
      setProgress(0);

      const formData = new FormData();
      formData.append("file", file);

      simulateProgress(0, 30);

      const validateRes = await api.post(
        "/products/bulk-upload-products/validate",
        formData,
        { timeout: 0 }
      );

      simulateProgress(30, 60);
      setStatus("validating");

      setDuplicates(validateRes.data.duplicates || []);
      setErrors(validateRes.data.errors || []);
      setValidRows(validateRes.data.validRows || []);

      if (validateRes.data.errors.length > 0) {
        setStatus("error");
        toast.error("Validation errors found");
        return;
      }

      if (validateRes.data.duplicateCount > 0) {
        setShowDuplicateModal(true);
        setStatus("waiting");
        return;
      }

      await confirmInsert(validateRes.data.validRows);

    } catch (err) {
      setStatus("error");
      toast.error("Upload failed");
    }
  };


  const confirmInsert = async (rows) => {
    try {
      setShowDuplicateModal(false);
      setStatus("Preparing upload...");
      setProgress(0);
      // simulateProgress(60, 90);
      // const restaurantId = localStorage.getItem("restaurantId");

      const res = await api.post(
        "/products/bulk-upload-products/confirm",
        { rows, restaurantId },
        {
          timeout: 0 // prevent axios cancel
        }
      );

      //  If backend says no data
      if (!res.data.success) {
        setStatus("idle");
        setProgress(0);
        toast.info(res.data.message);
        return;
      }

      //  If inserted count is 0 (extra safety)
      if (res.data.insertedCount === 0) {
        setStatus("idle");
        setProgress(0);
        toast.info("No new data to upload.");
        return;
      }

      // simulateProgress(90, 100);
      setStatus("success");
      toast.success(
        `Bulk upload completed. ${res.data.insertedCount} products added.`
      );

      setTimeout(() => {
        setStatus("idle");
        setProgress(0);
        setFile(null);
        setDuplicates([]);
        setErrors([]);
        setValidRows([]);
      }, 2000);

    } catch (err) {

      if (err.code === "ERR_CANCELED") {
        console.log("Request canceled by client");
        return;
      }

      if (err.response) {
        toast.error(err.response.data?.message || "Insert failed");
      } else {
        toast.error("Network error during upload");
      }

      setStatus("error");
    }
  };

  return (
    <div className="container">
      <PageHeader
        title="Bulk Product Upload"
        subtitle={
          <span className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
            Upload multiple products using a single file
          </span>
        }
        showBackButton={true}
        backTo={-1}
        action={
          <div className="relative flex">

            {/* Main Button */}
            <button
              onClick={() => downloadProductTemplate("xlsx")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-l-lg transition-colors duration-200 flex items-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>

              Download Template
            </button>

            {/* Arrow Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 rounded-r-lg border-l border-blue-500 flex items-center"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Dropdown */}
            {isMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsMenuOpen(false)}
                />

                <div className="absolute right-0 top-12 w-56 rounded-xl shadow-xl bg-white border border-gray-100 z-50 overflow-hidden">
                  <div className="py-1">
                    {templateOptions.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          option.command();
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                      >
                        <i className={`${option.icon} mr-3 text-gray-400`} />
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        }
      />


      <ShadowContainer>
        <form className="space-y-8" onSubmit={handleSubmit}>

          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-gray-900">
              Upload File
            </h2>
            <p className="text-sm text-gray-600">
              Drag & drop your Excel/CSV file below or click to select.
            </p>
          </div>

          {/* Drag Drop */}
          <div
            className={`w-full border-2 border-dashed rounded-xl p-8 text-center transition ${dragActive
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 bg-white"
              }`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="hidden"
              id="fileUpload"
            />

            <label
              htmlFor="fileUpload"
              className="cursor-pointer flex flex-col items-center gap-3"
            >
              <div className="text-gray-500">
                {dragActive ? "Drop file here" : "Drag & drop file here"}
              </div>
              <div className="text-sm text-blue-600 font-medium">
                or click to browse
              </div>
            </label>

            {file && (
              <div className="mt-4 text-sm text-gray-700 font-medium">
                Selected: {file.name}
              </div>
            )}

            <p className="mt-3 text-xs text-gray-500">
              Supported formats: .xlsx, .xls, .csv
            </p>
          </div>

          {/* Progress Bar */}
          {status !== "idle" && (
            <div className="w-full">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden mt-2">
                <div
                  className="h-2 bg-blue-600 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {status}
              </div>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-700">
                Validation Errors
              </h4>
              <ul className="text-sm mt-2 space-y-1 max-h-40 overflow-y-auto">
                {errors.map((err, i) => (
                  <li key={i}>
                    Row {err.row}: {err.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setFile(null);
                setStatus("idle");
                setProgress(0);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Clear
            </button>

            <button
              type="submit"
              disabled={status === "saving"}
              className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm"
            >
              Upload Products
            </button>
          </div>
        </form>
      </ShadowContainer>

      {/* Duplicate Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl w-[500px] max-h-[80vh] flex flex-col">

            <h3 className="font-semibold text-lg mb-2 text-red-600">
              Duplicate Products Found
            </h3>

            <p className="text-sm mb-3">
              {duplicates.length} product(s) already exist in database.
              Review them below.
            </p>

            {/* Duplicate List */}
            <div className="border rounded-lg p-3 overflow-y-auto flex-1 bg-gray-50">
              {duplicates.map((dup, index) => (
                <div
                  key={index}
                  className="border-b last:border-none py-2 text-sm"
                >
                  <div>
                    <span className="font-semibold">Row:</span> {dup.row}
                  </div>
                  <div>
                    <span className="font-semibold">Product:</span> {dup.productName}
                  </div>
                  <div>
                    <span className="font-semibold">Existing Category:</span>{" "}
                    {dup.existingCategory || "N/A"}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setStatus("idle");
                }}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={() => confirmInsert(validRows)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg"
              >
                Skip Duplicates & Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductBulkUpload;